import { randomUUID } from './api/client';
import { realtime } from './realtime';
import { applyIceServers, currentIceServers } from './rtcIce';
import { rtcApp } from './rtc';

export type GroupCallV2Phase = 'idle' | 'incoming' | 'connecting' | 'active' | 'ended';

export interface GroupCallV2Member {
  userId: string;
  sessionRef: string;
  joinSeq: number;
}

export interface GroupCallV2Handlers {
  onPhase: (phase: GroupCallV2Phase) => void;
  onRoster: (members: GroupCallV2Member[]) => void;
  onRemoteStream: (userId: string, stream: MediaStream) => void;
  onPeerLeft: (userId: string) => void;
  onPeerMuteState: (userId: string, kind: 'audio' | 'video', muted: boolean) => void;
  onJoinRejected: (reason: string) => void;
  onFinished: (reason: string) => void;
}

export interface GroupCallV2Signal {
  type?: unknown;
  v?: unknown;
  callKind?: unknown;
  chatId?: unknown;
  callId?: unknown;
  clientCallToken?: unknown;
  fromUserId?: unknown;
  fromSessionRef?: unknown;
  toSessionRef?: unknown;
  sessionRef?: unknown;
  members?: unknown;
  participant?: unknown;
  video?: unknown;
  deadlineAt?: unknown;
  state?: unknown;
  reason?: unknown;
  detail?: unknown;
  negId?: unknown;
  sdp?: unknown;
  candidate?: unknown;
  kind?: unknown;
  muted?: unknown;
}

interface GroupPeer {
  member: GroupCallV2Member;
  pc: RTCPeerConnection;
  ownNeg: number;
  remoteNeg: number;
  currentNeg: number;
  remoteDescriptionSet: boolean;
  pendingCandidates: RTCIceCandidateInit[];
  negotiating: boolean;
}

const isNegId = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 1_000_000_000;

/** v2 group session: one RTCPeerConnection per admitted remote session. */
export class GroupCallV2Session {
  readonly chatId: string;
  readonly meId: string;
  localStream?: MediaStream;
  private handlers: GroupCallV2Handlers;
  private members = new Map<string, GroupCallV2Member>();
  private peers = new Map<string, GroupPeer>();
  private phase: GroupCallV2Phase = 'idle';
  private callId: string | null = null;
  private callToken: string | null = null;
  private sessionRef: string | null = null;
  private video = false;
  private deadlineAt = 0;
  private joined = false;
  private creator = false;
  private pendingEnd = false;
  private destroyed = false;
  private heartbeatTimer?: ReturnType<typeof setInterval>;

  constructor(
    chatId: string,
    meId: string,
    handlers: GroupCallV2Handlers,
    private readonly publish: (destination: string, body: unknown) => boolean = (destination, body) => realtime.publish(destination, body),
  ) {
    this.chatId = chatId;
    this.meId = meId;
    this.handlers = handlers;
  }

  get isDestroyed(): boolean { return this.destroyed; }
  get isCreator(): boolean { return this.creator; }
  get isIncoming(): boolean { return this.phase === 'incoming'; }
  get currentCallId(): string | null { return this.callId; }
  get wantsVideo(): boolean { return this.video; }

  async startWithMedia(audio: boolean, video: boolean): Promise<void> {
    const stream = await navigator.mediaDevices.getUserMedia({ audio, video });
    if (this.destroyed) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    this.localStream = stream;
  }

  startOutgoing(video: boolean, roomId: string | null = null): void {
    if (this.destroyed || this.phase !== 'idle') return;
    this.creator = true;
    this.video = video;
    this.callToken = randomUUID();
    this.setPhase('connecting');
    this.send('call', { clientCallToken: this.callToken, video, ...(roomId ? { roomId } : {}) });
  }

  receiveIncoming(callId: string, video: boolean, deadlineAt = 0): void {
    if (this.destroyed || this.phase !== 'idle') return;
    this.callId = callId;
    this.video = video;
    this.deadlineAt = deadlineAt;
    this.setPhase('incoming');
  }

  join(): void {
    if (this.destroyed || this.phase !== 'incoming' || !this.callId) return;
    if (this.deadlineAt > 0 && Date.now() >= this.deadlineAt) {
      this.finish('timeout', false);
      return;
    }
    this.joined = true;
    this.setPhase('connecting');
    this.send('join');
  }

  decline(): void {
    if (this.destroyed || this.phase !== 'incoming' || !this.callId) return;
    this.send('decline');
    this.finish('declined');
  }

  /** Creator ends the group; other participants leave only their own mesh session. */
  leaveOrEnd(): void {
    if (this.destroyed) return;
    if (!this.callId) {
      this.pendingEnd = true;
      return;
    }
    this.finish(this.creator ? 'ended' : 'left');
  }

  toggleMic(): boolean {
    const track = this.localStream?.getAudioTracks()[0];
    if (!track) return false;
    track.enabled = !track.enabled;
    this.send('mute-state', { kind: 'audio', muted: !track.enabled });
    return !track.enabled;
  }

  toggleCamera(): boolean {
    const track = this.localStream?.getVideoTracks()[0];
    if (!track) return false;
    track.enabled = !track.enabled;
    this.send('mute-state', { kind: 'video', muted: !track.enabled });
    return !track.enabled;
  }

  onEvent(raw: GroupCallV2Signal): void {
    if (this.destroyed || raw.v !== 2 || raw.callKind !== 'group' || raw.chatId !== this.chatId) return;
    if (raw.type === 'incoming') {
      if (raw.fromUserId === this.meId || typeof raw.callId !== 'string') return;
      if (this.phase === 'idle') this.receiveIncoming(raw.callId, raw.video === true,
        typeof raw.deadlineAt === 'number' ? raw.deadlineAt : 0);
      return;
    }
    if (raw.type === 'created') {
      if (this.phase !== 'connecting' || typeof raw.callId !== 'string' || raw.clientCallToken !== this.callToken) return;
      this.callId = raw.callId;
      this.sessionRef = typeof raw.sessionRef === 'string' ? raw.sessionRef : null;
      this.joined = true;
      if (this.sessionRef) this.members.set(this.sessionRef, { userId: this.meId, sessionRef: this.sessionRef, joinSeq: 1 });
      if (this.pendingEnd) {
        this.send('end');
        this.finish('ended', false);
        return;
      }
      this.startHeartbeat();
      this.setPhase('active');
      return;
    }
    if (raw.type === 'call_rejected' && this.phase === 'connecting') {
      const reason = typeof raw.reason === 'string' ? raw.reason : 'unknown';
      if (reason === 'chat_busy' && typeof raw.callId === 'string') {
        this.callId = raw.callId;
        this.creator = false;
        this.joined = true;
        this.send('join');
        return;
      }
      this.handlers.onJoinRejected(reason);
      this.finish(reason, false);
      return;
    }
    if (!this.callId || raw.callId !== this.callId) return;
    switch (raw.type) {
      case 'roster': this.onRoster(raw); break;
      case 'member_joined': this.onMemberJoined(raw); break;
      case 'join_rejected':
        this.handlers.onJoinRejected(typeof raw.reason === 'string' ? raw.reason : 'unknown');
        this.finish(typeof raw.reason === 'string' ? raw.reason : 'error');
        break;
      case 'offer': void this.onOffer(raw); break;
      case 'answer': void this.onAnswer(raw); break;
      case 'ice': void this.onIce(raw); break;
      case 'left': this.onLeft(raw); break;
      case 'mute-state': this.onMute(raw); break;
      case 'state': if (raw.state === 'active') this.setPhase('active'); break;
      case 'incoming_closed':
        if (this.phase === 'incoming') this.finish(typeof raw.reason === 'string' ? raw.reason : 'ended');
        break;
      case 'ended': this.finish(typeof raw.reason === 'string' ? raw.reason : 'ended', false); break;
    }
  }

  destroy(): void {
    this.finish('ended', false);
  }

  private onRoster(raw: GroupCallV2Signal): void {
    if (typeof raw.sessionRef === 'string') this.sessionRef = raw.sessionRef;
    const next = new Map<string, GroupCallV2Member>();
    if (Array.isArray(raw.members)) {
      for (const item of raw.members) {
        if (!item || typeof item !== 'object') continue;
        const member = item as Partial<GroupCallV2Member>;
        if (typeof member.userId !== 'string' || typeof member.sessionRef !== 'string' || typeof member.joinSeq !== 'number') continue;
        next.set(member.sessionRef, { userId: member.userId, sessionRef: member.sessionRef, joinSeq: member.joinSeq });
      }
    }
    this.members = next;
    this.joined = true;
    this.startHeartbeat();
    this.setPhase('active');
    this.publishRoster();
    const self = this.sessionRef ? this.members.get(this.sessionRef) : undefined;
    if (!self) return;
    for (const member of this.members.values()) {
      if (member.sessionRef === self.sessionRef) continue;
      this.ensurePeer(member);
      if (self.joinSeq > member.joinSeq) void this.makeOffer(member.sessionRef);
    }
  }

  private onMemberJoined(raw: GroupCallV2Signal): void {
    if (!raw.participant || typeof raw.participant !== 'object') return;
    const participant = raw.participant as Partial<GroupCallV2Member>;
    if (typeof participant.userId !== 'string' || typeof participant.sessionRef !== 'string' || typeof participant.joinSeq !== 'number') return;
    this.members.set(participant.sessionRef, {
      userId: participant.userId,
      sessionRef: participant.sessionRef,
      joinSeq: participant.joinSeq,
    });
    this.publishRoster();
  }

  private async onOffer(raw: GroupCallV2Signal): Promise<void> {
    if (typeof raw.fromSessionRef !== 'string' || raw.toSessionRef !== this.sessionRef ||
        !isNegId(raw.negId) || typeof raw.sdp !== 'string') return;
    const member = this.members.get(raw.fromSessionRef);
    if (!member) return;
    const peer = this.ensurePeer(member);
    if (raw.negId <= peer.remoteNeg) return;
    peer.remoteNeg = raw.negId;
    peer.currentNeg = raw.negId;
    try {
      await applyIceServers(peer.pc);
      await peer.pc.setRemoteDescription({ type: 'offer', sdp: raw.sdp });
      peer.remoteDescriptionSet = true;
      peer.remoteNeg = raw.negId;
      peer.currentNeg = raw.negId;
      await this.flushCandidates(peer);
      const answer = await peer.pc.createAnswer();
      this.send('answer', { toSessionRef: member.sessionRef, negId: raw.negId, sdp: answer.sdp });
      await peer.pc.setLocalDescription(answer);
    } catch (error) {
      console.warn('[rtc-group-v2] offer error', error);
      this.removePeer(member.sessionRef);
    }
  }

  private async onAnswer(raw: GroupCallV2Signal): Promise<void> {
    if (typeof raw.fromSessionRef !== 'string' || raw.toSessionRef !== this.sessionRef || !isNegId(raw.negId) || typeof raw.sdp !== 'string') return;
    const peer = this.peers.get(raw.fromSessionRef);
    if (!peer || raw.negId !== peer.ownNeg) return;
    try {
      await peer.pc.setRemoteDescription({ type: 'answer', sdp: raw.sdp });
      peer.remoteDescriptionSet = true;
      peer.currentNeg = raw.negId;
      await this.flushCandidates(peer);
    } catch (error) {
      console.warn('[rtc-group-v2] answer error', error);
      this.removePeer(raw.fromSessionRef);
    }
  }

  private async onIce(raw: GroupCallV2Signal): Promise<void> {
    if (typeof raw.fromSessionRef !== 'string' || raw.toSessionRef !== this.sessionRef || !isNegId(raw.negId)) return;
    const peer = this.peers.get(raw.fromSessionRef);
    if (!peer || raw.negId !== peer.currentNeg || !raw.candidate || typeof raw.candidate !== 'object') return;
    try {
      if (peer.remoteDescriptionSet) await peer.pc.addIceCandidate(raw.candidate as RTCIceCandidateInit);
      else peer.pendingCandidates.push(raw.candidate as RTCIceCandidateInit);
    } catch { /* candidate may race teardown */ }
  }

  private onLeft(raw: GroupCallV2Signal): void {
    if (typeof raw.sessionRef !== 'string') return;
    this.removePeer(raw.sessionRef);
    this.members.delete(raw.sessionRef);
    this.publishRoster();
  }

  private onMute(raw: GroupCallV2Signal): void {
    if (typeof raw.sessionRef !== 'string' || (raw.kind !== 'audio' && raw.kind !== 'video') || typeof raw.muted !== 'boolean') return;
    const member = this.members.get(raw.sessionRef);
    if (member) this.handlers.onPeerMuteState(member.userId, raw.kind, raw.muted);
  }

  private ensurePeer(member: GroupCallV2Member): GroupPeer {
    const existing = this.peers.get(member.sessionRef);
    if (existing) return existing;
    const pc = new RTCPeerConnection({ iceServers: currentIceServers() });
    const peer: GroupPeer = {
      member,
      pc,
      ownNeg: 0,
      remoteNeg: 0,
      currentNeg: 0,
      remoteDescriptionSet: false,
      pendingCandidates: [],
      negotiating: false,
    };
    pc.onicecandidate = (event) => {
      if (peer.currentNeg === 0) return;
      this.send('ice', { toSessionRef: member.sessionRef, negId: peer.currentNeg, candidate: event.candidate?.toJSON() ?? null });
    };
    pc.ontrack = (event) => {
      if (event.streams[0]) this.handlers.onRemoteStream(member.userId, event.streams[0]);
    };
    this.localStream?.getTracks().forEach((track) => pc.addTrack(track, this.localStream!));
    this.peers.set(member.sessionRef, peer);
    return peer;
  }

  private async makeOffer(sessionRef: string): Promise<void> {
    const peer = this.peers.get(sessionRef);
    if (!peer || peer.negotiating || peer.pc.signalingState !== 'stable') return;
    const self = this.sessionRef ? this.members.get(this.sessionRef) : undefined;
    const remote = this.members.get(sessionRef);
    if (!self || !remote || self.joinSeq <= remote.joinSeq) return;
    peer.negotiating = true;
    try {
      await applyIceServers(peer.pc);
      const offer = await peer.pc.createOffer();
      peer.ownNeg = Math.max(peer.ownNeg, peer.remoteNeg) + 1;
      peer.currentNeg = peer.ownNeg;
      this.send('offer', { toSessionRef: sessionRef, negId: peer.ownNeg, sdp: offer.sdp });
      await peer.pc.setLocalDescription(offer);
    } catch (error) {
      console.warn('[rtc-group-v2] offer creation error', error);
      this.removePeer(sessionRef);
    } finally {
      peer.negotiating = false;
    }
  }

  private async flushCandidates(peer: GroupPeer): Promise<void> {
    const pending = peer.pendingCandidates;
    peer.pendingCandidates = [];
    for (const candidate of pending) {
      try { await peer.pc.addIceCandidate(candidate); } catch { /* stale candidate */ }
    }
  }

  private removePeer(sessionRef: string): void {
    const peer = this.peers.get(sessionRef);
    if (!peer) return;
    peer.pc.onicecandidate = null;
    peer.pc.ontrack = null;
    peer.pc.close();
    this.peers.delete(sessionRef);
    this.handlers.onPeerLeft(peer.member.userId);
  }

  private startHeartbeat(): void {
    if (this.heartbeatTimer != null || !this.callId) return;
    this.heartbeatTimer = setInterval(() => this.send('hb'), 10_000);
  }

  private publishRoster(): void {
    const remote = [...this.members.values()].filter((member) => member.sessionRef !== this.sessionRef);
    this.handlers.onRoster(remote);
  }

  private send(type: string, fields: Record<string, unknown> = {}): boolean {
    return this.publish(rtcApp(this.chatId), {
      v: 2,
      callKind: 'group',
      type,
      chatId: this.chatId,
      ...(this.callId ? { callId: this.callId } : {}),
      ...fields,
    });
  }

  private setPhase(phase: GroupCallV2Phase): void {
    this.phase = phase;
    this.handlers.onPhase(phase);
  }

  private finish(reason: string, notify = true): void {
    if (this.destroyed) return;
    if (notify && this.callId && this.joined) this.send(this.creator ? 'end' : 'leave');
    if (this.heartbeatTimer != null) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = undefined;
    this.destroyed = true;
    this.setPhase('ended');
    for (const ref of [...this.peers.keys()]) this.removePeer(ref);
    this.localStream?.getTracks().forEach((track) => track.stop());
    this.localStream = undefined;
    this.handlers.onFinished(reason);
  }
}
